import {
  formatDateTimeISOTimeZone,
  localToUtc,
  Logging,
  stringToDate,
} from '@vcd-ecm-ui/Common';
import { ColumnFilterItem } from '../..';
import {
  FilterModelApi,
  GenericGridRowData,
  GridFilter,
  GridSort,
  SortModelApi,
} from '../GenericGrid/Interfaces';
import {
  FilterConditionEnum,
  FilterOptionEnum,
  getSearchFilterAsObject,
  getSearchFilterAsString,
  SearchFilterName,
  SearchFilterType,
} from './SearchFilterProps.interface';
import {
  getSelectFilterAsObject,
  SelectFilterName,
  SelectFilterType,
} from './SelectFilterProps.interface';

// Filter Type
export enum FilterTypeEnum {
  Text = 'text',
  Date = 'date',
  Number = 'number',
  Decimal = 'decimal',
  Boolean = 'boolean',
}

// Get the Unique Filter Values based on the Rows
export const getUniqueFilters = (rows: GenericGridRowData[], property: string, isDate = false): ColumnFilterItem[] => {
  if (rows) {
    const filterMap = new Map();

    rows.forEach((row) => {
      // The Value can be in the Row data or Computed value
      const value = row.rowData[property] || row[property];

      if (value) {
        const text = isDate ? (localToUtc(stringToDate(value))).toLocaleDateString() : value;
        filterMap.set(value, { text, value });
      }
    });

    return [...filterMap.values()];
  }

  return undefined;
};

// Client Side Filtering
export const searchOrSelectFilter = (value: string, row: GenericGridRowData): boolean => {
  Logging.Info('searchOrSelectFilter - TO BE COMPLETED', value); // FIX ME

  return true;
};

// Get the Search or Select Filter from the Grid Filter
export const getFilter = (gridFilter: GridFilter): SearchFilterType | SelectFilterType => {
  // Check if the Filter is a Search Filter
  const searchFilter: SearchFilterType = getSearchFilterAsObject(gridFilter.value);

  // Filter is a Search Filter
  if (searchFilter !== null) {
    return searchFilter;
  }

  // Check if the Filter is a Select Filter
  const selectFilter: SelectFilterType = getSelectFilterAsObject(gridFilter.value);

  // Filter is a Select Filter
  if (selectFilter !== null) {
    return selectFilter;
  }

  return null;
};

// Convert the Filter Option to Option used by the Server
const filterOptionMap:{[index: string]: string} = {
  contains: 'ct',
  notContains: 'nct',
  equals: 'eq',
  notEquals: 'ne',
  startsWith: 'sw',
  endsWith: 'ew',
  greaterThan: 'gt',
  greaterThanOrEquals: 'ge',
  lessThan: 'lt',
  lessThanOrEquals: 'le',
};

// Convert the Filter Condition to Condition used by the Server
const filterConditionMap:{[index: string]: string} = {
  AND: 'and',
  OR: 'or',
  NOT: 'not',
};

// Render the Filter Value based on the Filter Type
const renderFilterValue = (type: FilterTypeEnum, value: string): string => {
  switch (type) {
    case FilterTypeEnum.Text:
    case FilterTypeEnum.Date:
    case FilterTypeEnum.Boolean:
      return `'${value}'`;
    case FilterTypeEnum.Number:
    case FilterTypeEnum.Decimal:
      return value;
    default:
      Logging.Error('Unhandled Filter Type:', type, value);
      return null;
  }
};

// Add days to a Date
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Render the Filter Option as Timestamp.  Includes the full day
const renderTimeStampFilter = (
  column: string,
  type: FilterTypeEnum,
  filterOption: FilterOptionEnum,
  valueFrom: string,
  valueTo: string,
): string => {
  // Convert the From Date to a UTC Date.  Original Date entered was: "2021-03-25"
  const valueFromStart: Date = localToUtc(stringToDate(valueFrom));

  // Add 1 day to the From Date to form the To date
  const valueFromEnd: Date = addDays(valueFromStart, 1);

  // Format the From Date with the TimeZone, e.g. "2021-03-25T00:00:00.000-04:00"
  const valueFromStartString = formatDateTimeISOTimeZone(valueFromStart);

  // Format the To Date with the TimeZone, e.g. "2021-03-26T00:00:00.000-04:00"
  const valueFromEndString = formatDateTimeISOTimeZone(valueFromEnd);

  // Convert the From Date to a UTC Date.  Original Date entered was: "2021-03-25"
  const valueToStart: Date = localToUtc(stringToDate(valueTo));

  // Add 1 day to the From Date to form the To date
  const valueToEnd: Date = addDays(valueToStart, 1);

  // Format the From Date with the TimeZone, e.g. "2021-03-25T00:00:00.000-04:00"
  const valueToStartString = formatDateTimeISOTimeZone(valueToStart);

  // Format the To Date with the TimeZone, e.g. "2021-03-26T00:00:00.000-04:00"
  const valueToEndString = formatDateTimeISOTimeZone(valueToEnd);

  switch (filterOption) {
    case FilterOptionEnum.EQUALS:
      // eslint-disable-next-line max-len
      return `${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN_OR_EQUALS]} '${valueFromStartString}' ${filterConditionMap[FilterConditionEnum.AND]} ${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN]} '${valueFromEndString}'`;
    case FilterOptionEnum.NOT_EQUALS:
      // eslint-disable-next-line max-len
      return `(${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN]} '${valueFromStartString}' ${filterConditionMap[FilterConditionEnum.OR]} ${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN_OR_EQUALS]} '${valueFromEndString}')`;
    case FilterOptionEnum.LESS_THAN:
      return `${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN]} '${valueFromStartString}'`;
    case FilterOptionEnum.LESS_THAN_OR_EQUALS:
      return `${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN_OR_EQUALS]} '${valueFromEndString}'`;
    case FilterOptionEnum.GREATER_THAN:
      return `${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN]} '${valueFromEndString}'`;
    case FilterOptionEnum.GREATER_THAN_OR_EQUALS:
      return `${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN_OR_EQUALS]} '${valueFromStartString}'`;
    case FilterOptionEnum.IN_RANGE_INCLUDED:
      // eslint-disable-next-line max-len
      return `${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN_OR_EQUALS]} '${valueFromStartString}' ${filterConditionMap[FilterConditionEnum.AND]} ${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN_OR_EQUALS]} '${valueToEndString}'`;
    case FilterOptionEnum.IN_RANGE_EXCLUDED:
      // eslint-disable-next-line max-len
      return `${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN]} '${valueFromStartString}' ${filterConditionMap[FilterConditionEnum.AND]} ${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN]} '${valueToEndString}'`;
    case FilterOptionEnum.IS_EMPTY:
      return `${column} ${filterOptionMap[FilterOptionEnum.EQUALS]} ''`;
    case FilterOptionEnum.IS_NOT_EMPTY:
      return `${column} ${filterOptionMap[FilterOptionEnum.NOT_EQUALS]} ''`;
    case FilterOptionEnum.IS_NULL:
      return `${column} ${filterOptionMap[FilterOptionEnum.NOT_CONTAINS]} ''`;
    case FilterOptionEnum.IS_NOT_NULL:
      return `${column} ${filterOptionMap[FilterOptionEnum.CONTAINS]} ''`;
    default:
      Logging.Error('Unhandled Timestamp Filter Option:', column, type, filterOption, valueFrom, valueTo);
      return null;
  }
};

// Render the Filter Option
const renderFilterOption = (
  column: string,
  type: FilterTypeEnum,
  filterOption: FilterOptionEnum,
  valueFrom: string,
  valueTo: string,
  isTimestamp: boolean,
): string => {
  if (isTimestamp) {
    return renderTimeStampFilter(column, type, filterOption, valueFrom, valueTo);
  }

  switch (filterOption) {
    case FilterOptionEnum.CONTAINS:
    case FilterOptionEnum.NOT_CONTAINS:
    case FilterOptionEnum.EQUALS:
    case FilterOptionEnum.NOT_EQUALS:
    case FilterOptionEnum.STARTS_WITH:
    case FilterOptionEnum.ENDS_WITH:
    case FilterOptionEnum.LESS_THAN:
    case FilterOptionEnum.LESS_THAN_OR_EQUALS:
    case FilterOptionEnum.GREATER_THAN:
    case FilterOptionEnum.GREATER_THAN_OR_EQUALS:
      return `${column} ${filterOptionMap[filterOption]} ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.IN_RANGE_INCLUDED:
      // eslint-disable-next-line max-len
      return `(${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN_OR_EQUALS]} ${renderFilterValue(type, valueFrom)}) ${filterConditionMap[FilterConditionEnum.AND]} (${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN_OR_EQUALS]} ${renderFilterValue(type, valueTo)})`;
    case FilterOptionEnum.IN_RANGE_EXCLUDED:
      // eslint-disable-next-line max-len
      return `(${column} ${filterOptionMap[FilterOptionEnum.GREATER_THAN]} ${renderFilterValue(type, valueFrom)}) ${filterConditionMap[FilterConditionEnum.AND]} (${column} ${filterOptionMap[FilterOptionEnum.LESS_THAN]} ${renderFilterValue(type, valueTo)})`;
    case FilterOptionEnum.IS_EMPTY:
      return `${column} ${filterOptionMap[FilterOptionEnum.EQUALS]} ''`;
    case FilterOptionEnum.IS_NOT_EMPTY:
      return `${column} ${filterOptionMap[FilterOptionEnum.NOT_EQUALS]} ''`;
    case FilterOptionEnum.IS_NULL:
      return `${column} ${filterOptionMap[FilterOptionEnum.NOT_CONTAINS]} ''`;
    case FilterOptionEnum.IS_NOT_NULL:
      return `${column} ${filterOptionMap[FilterOptionEnum.CONTAINS]} ''`;
    default:
      Logging.Error('Unhandled Filter Option:', column, type, filterOption, valueFrom, valueTo);
      return null;
  }
};

// Render the Filter as a string ready for inclusion in the API's Filter Clause.
const renderFilter = (gridFilter: GridFilter): string => {
  let filterString: string = null;

  const searchOrSelect: SearchFilterType | SelectFilterType = getFilter(gridFilter);

  // Filter is a Search Filter
  if (searchOrSelect.name === SearchFilterName) {
    // Simple Filter
    if (!searchOrSelect.joinCondition || (searchOrSelect.joinCondition === FilterConditionEnum.NONE)) {
      // eslint-disable-next-line max-len
      filterString = `${renderFilterOption(gridFilter.column, searchOrSelect.type, searchOrSelect.option1, searchOrSelect.valueFrom1, searchOrSelect.valueTo1, searchOrSelect.isTimestamp)}`;
    } else { // Compound Filter
      // eslint-disable-next-line max-len
      filterString = `(${renderFilterOption(gridFilter.column, searchOrSelect.type, searchOrSelect.option1, searchOrSelect.valueFrom1, searchOrSelect.valueTo1, searchOrSelect.isTimestamp)} ${filterConditionMap[searchOrSelect.joinCondition]} ${renderFilterOption(gridFilter.column, searchOrSelect.type, searchOrSelect.option2, searchOrSelect.valueFrom2, searchOrSelect.valueTo2, searchOrSelect.isTimestamp)})`;
    }

    Logging.Debug('Search Filter:', gridFilter.column, searchOrSelect, filterString);
    // Filter is a Select Filter
  } else if (searchOrSelect.name === SelectFilterName) {
    // Number of Specific Selected Values
    const numberValues: number = searchOrSelect.values.length;

    if (!searchOrSelect.search && searchOrSelect.selectAll) {
      // Nothing to Filter.  All Selected
    } else {
      // Specific Values are Selected
      if (numberValues) {
        searchOrSelect.values.forEach((value: string) => {
          if (filterString) {
            filterString += ` ${filterConditionMap[FilterConditionEnum.OR]} `;
          } else {
            filterString = '';
          }

          let filterOption: FilterOptionEnum = FilterOptionEnum.EQUALS;
          let filterValue = value;

          // E.g. Change 'numCertificates eq 'true'  ->  'numCertificates ne 0'
          // E.g. Change 'numCertificates eq 'false''  ->  'numCertificates eq 0'
          if (searchOrSelect.convertBooleanToNumerical) {
            filterValue = '0';
            searchOrSelect.type = FilterTypeEnum.Number;

            if (value === 'true') {
              filterOption = FilterOptionEnum.NOT_EQUALS;
            }
          }

          filterString += `${renderFilterOption(gridFilter.column, searchOrSelect.type, filterOption, filterValue, null, false)}`;
        });

        filterString = numberValues > 1 ? `(${filterString})` : filterString;
      }

      Logging.Debug('Select Filter:', gridFilter.column, searchOrSelect, filterString);
    }
  // Unknown Filter
  } else {
    Logging.Error('Unknown Filter');
  }

  return filterString;
};

// Render the Filters as a string[] ready for inclusion in the API's Filter Clause.
export const renderFilters = (gridFilters: GridFilter[]): FilterModelApi => {
  if (gridFilters) {
    const filters: string [] = [];

    // Loop through all the Filters
    gridFilters.forEach((gridFilter: GridFilter) => {
      // Render each Filter
      filters.push(renderFilter(gridFilter));
    });

    return filters;
  }

  return null;
};

// Render the Sorters as a string[] ready for inclusion in the API's OrderBy Clause.
export const renderSorters = (gridSorters: GridSort[]): SortModelApi => {
  if (gridSorters) {
    const sorters: string [] = [];

    // Loop through all the Sorters
    gridSorters.forEach((gridSorter: GridSort) => {
      // Render each Sorter
      sorters.push(`${gridSorter.column} ${gridSorter.order}`);
    });

    return sorters;
  }

  return null;
};

// Get an User Friendly Description of the Filter Option
const getFilterOptionDescription = (
  type: FilterTypeEnum,
  filterOption: FilterOptionEnum,
  valueFrom: string,
  valueTo: string,
): string => {
  switch (filterOption) {
    case FilterOptionEnum.CONTAINS:
      return `Contains ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.NOT_CONTAINS:
      return `Not contains ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.EQUALS:
      return `Equals ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.NOT_EQUALS:
      return `Not equals ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.STARTS_WITH:
      return `Starts with ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.ENDS_WITH:
      return `Ends with ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.LESS_THAN:
      return `${type === FilterTypeEnum.Date ? 'Is before' : 'Less than'} ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.LESS_THAN_OR_EQUALS:
      return `${type === FilterTypeEnum.Date ? 'Is before or equal' : 'Less than or equals'} ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.GREATER_THAN:
      return `${type === FilterTypeEnum.Date ? 'Is after' : 'Greater than'} ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.GREATER_THAN_OR_EQUALS:
      return `${type === FilterTypeEnum.Date ? 'Is after or equals' : 'Greater than or equals'} ${renderFilterValue(type, valueFrom)}`;
    case FilterOptionEnum.IN_RANGE_INCLUDED:
      return `In range (included) ${renderFilterValue(type, valueFrom)} - ${renderFilterValue(type, valueTo)}`;
    case FilterOptionEnum.IN_RANGE_EXCLUDED:
      return `In range (excluded) ${renderFilterValue(type, valueFrom)} - ${renderFilterValue(type, valueTo)}`;
    case FilterOptionEnum.IS_EMPTY:
      return 'Is empty';
    case FilterOptionEnum.IS_NOT_EMPTY:
      return 'Is not empty';
    case FilterOptionEnum.IS_NULL:
      return 'Is null';
    case FilterOptionEnum.IS_NOT_NULL:
      return 'Is not null';
    default:
      return 'Unknown';
  }
};

// Get an User Friendly Description of the Search Filter
const getSearchFilterDescription = (searchFilter: SearchFilterType): string => {
  let filterString: string = null;

  // Simple Filter
  if (!searchFilter.joinCondition || (searchFilter.joinCondition === FilterConditionEnum.NONE)) {
    filterString = `${getFilterOptionDescription(searchFilter.type, searchFilter.option1, searchFilter.valueFrom1, searchFilter.valueTo1)}`;
  } else { // Compound Filter
    // eslint-disable-next-line max-len
    filterString = `${getFilterOptionDescription(searchFilter.type, searchFilter.option1, searchFilter.valueFrom1, searchFilter.valueTo1)} ${filterConditionMap[searchFilter.joinCondition]} ${getFilterOptionDescription(searchFilter.type, searchFilter.option2, searchFilter.valueFrom2, searchFilter.valueTo2)}`;
  }

  return filterString;
};

// Get an User Friendly Description of the Select Filter
const getSelectFilterDescription = (selectFilter: SelectFilterType): string => {
  const filterString: string = selectFilter.displayValues.join(', ');

  return filterString;
};

// Get an User Friendly Description of the Filter
export const getFilterDescription = (filter: GridFilter): string => {
  const searchOrSelect: SearchFilterType | SelectFilterType = getFilter(filter);

  // Filter is a Search Filter
  if (searchOrSelect.name === SearchFilterName) {
    return getSearchFilterDescription(searchOrSelect);
  }

  // Filter is a Select Filter
  if (searchOrSelect.name === SelectFilterName) {
    return getSelectFilterDescription(searchOrSelect);
  }

  return null;
};

// Get an User Friendly Description of the Sorter
export const getSorterDescription = (sorter: GridSort): string => {
  Logging.Debug('Sorter:', sorter);

  return sorter.order === 'asc' ? 'Ascending' : 'Descending';
};
